import { QueryOptions, WithRequired } from '@tanstack/react-query';

import {
  getCampaignMonthlyDeliverabilityMetrics,
  getCombinedMonthlyOptimizerMetrics,
  getCustomerFeedbacksForStore,
  getCustomerFeedbacksForUsecase,
  getFlowActionsForStoreKey,
  getFlowMessageExperimentAllocationConversionStats,
  getFlowMessageExperimentTargetMetrics,
  getFlowMessageOptimizerStats,
  getFlowMessagesForStoreKey,
  getFlowMonthlyOptimizerMetrics,
  getFlowsForStoreKey,
  getKlaviyoAccount,
  getKlaviyoAccountMetricsInSync,
  getLatestFlowExperimentPhaseForStoreKey,
  getOptimizerCampaignDeliverabilityExperimentMetrics,
  getOrganizationMembers,
  getPretesterInboxes,
  getSPOptimizerCampaignDeliverabilityBlockSetups,
  getSPOptimizerFlowMessageExperimentKlaviyoTemplates,
  getSPOptimizerFlowMessageExperimentMetricsForSPOptimizerStore,
  getSPOptimizerFlowMessageExperimentShardMetrics,
  getSPOptimizerFlowMessageExperimentVariantAggregatedMetrics,
  getSPOptimizerFlowMessageExperimentVariantsForExperiment,
  getSPOptimizerFlowMessageExperimentsForSPOptimizerStore,
  getSPOptimizerFlowMessageExperimentsForStore,
  getSPOptimizerStore,
  getSPOptimizerStoreAggregatedSummaryFlowMetrics,
  getSPOptimizerStoreDeliverabilityBlockSetups,
  getSPOptimizerStoreExcludePhrases,
  getSPOptimizerStoreIncludePhrases,
  getSPOptimizerStores,
  getSavedVideoScripts,
  getShopifyShop,
  getStoreAggregatedMetrics,
  getStoreDailyMetrics,
  getStores,
  getUsecase,
  getUsecasesForStore,
  postShopifyCartProductImages,
} from './methods';

const makeQuery = <F extends (...a: [...Parameters<F>]) => Promise<Awaited<ReturnType<F>>>>(
  apiMethod: F,
  otherOptions: (
    ...args: [...Parameters<F>]
  ) => Omit<WithRequired<QueryOptions<Awaited<ReturnType<F>>>, 'queryKey'>, 'queryFn'>,
): ((...args: [...Parameters<F>]) => WithRequired<QueryOptions<Awaited<ReturnType<F>>>, 'queryFn' | 'queryKey'>) => {
  return (...args) => ({
    queryFn: () => apiMethod(...args),
    ...otherOptions(...args),
  });
};

export const queries = {
  stores: makeQuery(getStores, () => ({
    queryKey: ['stores'],
  })),
  usecase: makeQuery(getUsecase, (organization: string, usecaseId: number) => ({
    queryKey: ['organization', organization, 'usecase', usecaseId],
  })),
  usecasesForStore: makeQuery(
    getUsecasesForStore,
    (organization: string | undefined, storeKey: string | undefined) => ({
      queryKey: ['organization', organization, 'usecases', storeKey],
    }),
  ),
  spOptimizerStores: makeQuery(getSPOptimizerStores, (organization: string) => ({
    queryKey: ['organization', organization, 'spOptimizerStores'],
  })),
  spOptimizerStore: makeQuery(getSPOptimizerStore, (organization: string, spOptimizerStoreID: number) => ({
    queryKey: ['organization', organization, 'spOptimizerStore', spOptimizerStoreID],
  })),
  spOptimizerStoreExcludePhrases: makeQuery(
    getSPOptimizerStoreExcludePhrases,
    (organization: string, spOptimizerStoreID: number) => ({
      queryKey: ['organization', organization, 'spOptimizerStoreExcludePhrases', spOptimizerStoreID],
    }),
  ),
  spOptimizerStoreIncludePhrases: makeQuery(
    getSPOptimizerStoreIncludePhrases,
    (organization: string, spOptimizerStoreID: number) => ({
      queryKey: ['organization', organization, 'spOptimizerStoreIncludePhrases', spOptimizerStoreID],
    }),
  ),
  spOptimizerFlowMessageExperimentShardMetrics: makeQuery(
    getSPOptimizerFlowMessageExperimentShardMetrics,
    (organization: string) => ({
      queryKey: ['organization', organization, 'spOptimizerFlowMessageExperimentShardMetrics'],
    }),
  ),
  spOptimizerStoreDeliverabilityBlockSetups: makeQuery(getSPOptimizerStoreDeliverabilityBlockSetups, () => ({
    queryKey: ['spOptimizerStoreDeliverabilityBlockSetups'],
  })),
  spOptimizerCampaignDeliverabilityBlockSetups: makeQuery(getSPOptimizerCampaignDeliverabilityBlockSetups, () => ({
    queryKey: ['spOptimizerCampaignDeliverabilityBlockSetups'],
  })),
  storeAggregatedMetrics: makeQuery(
    getStoreAggregatedMetrics,
    (organization: string, storeKey: string, days: number, useCaseId?: number) => ({
      queryKey: ['organization', organization, 'store', storeKey, 'usecase', useCaseId, 'aggregate-metrics', days],
    }),
  ),
  storeDailyMetrics: makeQuery(
    getStoreDailyMetrics,
    (organization: string, storeKey: string, days: number, useCaseId?: number) => ({
      queryKey: ['organization', organization, 'store', storeKey, 'usecase', useCaseId, 'daily-metrics', days],
    }),
  ),
  storeFlowMonthlyOptimizerMetrics: makeQuery(
    getFlowMonthlyOptimizerMetrics,
    (organization: string | undefined, spOptimizerStoreID: number | undefined) => ({
      queryKey: ['organization', organization, 'spOptimizerStore', spOptimizerStoreID, 'flow', 'monthly-metrics'],
    }),
  ),
  storeCampaignMonthlyDeliverabilityMetrics: makeQuery(
    getCampaignMonthlyDeliverabilityMetrics,
    (organization: string, spOptimizerStoreId: number | undefined) => ({
      queryKey: [
        'organization',
        organization,
        'spOptimizerStore',
        spOptimizerStoreId,
        'campaign-deliverability',
        'monthly-metrics',
      ],
    }),
  ),
  storeCombinedMonthlyOptimizerMetrics: makeQuery(
    getCombinedMonthlyOptimizerMetrics,
    (
      organization: string,
      spOptimizerStoreId: number | undefined,
      includeCampaigns?: boolean,
      includeFlows?: boolean,
    ) => ({
      queryKey: [
        'organization',
        organization,
        'spOptimizerStore',
        spOptimizerStoreId,
        'combined',
        'monthly-metrics',
        includeCampaigns,
        includeFlows,
      ],
    }),
  ),
  flowMessageExperimentAllocationConversionStats: makeQuery(
    getFlowMessageExperimentAllocationConversionStats,
    (organization: string, spOptimizerFlowMessageExperimentId: number) => ({
      queryKey: [
        'organization',
        organization,
        'flow-message-experiment-allocation-conversion-stats',
        spOptimizerFlowMessageExperimentId,
      ],
    }),
  ),
  flowMessageExperimentTargetMetrics: makeQuery(
    getFlowMessageExperimentTargetMetrics,
    (organization: string, spOptimizerFlowMessageExperimentId: number) => ({
      queryKey: [
        'organization',
        organization,
        'flow-message-experiment-target-metrics',
        spOptimizerFlowMessageExperimentId,
      ],
    }),
  ),
  customerFeedbacksForUsecase: makeQuery(
    getCustomerFeedbacksForUsecase,
    (organization: string, usecaseId: number, page = 1, pageSize = 10) => ({
      queryKey: ['organization', organization, 'usecase', 'user-feedback', usecaseId, page, pageSize],
    }),
  ),
  customerFeedbacksForStore: makeQuery(
    getCustomerFeedbacksForStore,
    (organization: string, storeId: number | undefined, page = 1, pageSize = 25) => ({
      queryKey: ['organization', organization, 'store', 'user-feedback', storeId, page, pageSize],
    }),
  ),
  klaviyoAccount: makeQuery(getKlaviyoAccount, (organization: string, klaviyoAccountId: number) => ({
    queryKey: ['organization', organization, 'klaviyo-account', klaviyoAccountId],
  })),
  shopifyShop: makeQuery(getShopifyShop, (organization: string, shopifyShopId: number) => ({
    queryKey: ['organization', organization, 'shopify-shop', shopifyShopId],
  })),
  shopifyCartProductImages: makeQuery(
    postShopifyCartProductImages,
    (
      organization: string,
      storeKey: string,
      shopifyCheckoutId: string | undefined | null,
      shopifyCheckoutToken: string | undefined | null,
      shopifyProductIds: string[],
    ) => ({
      queryKey: [
        'organization',
        organization,
        'store',
        storeKey,
        'shopify-cart-product-images',
        shopifyCheckoutId,
        shopifyCheckoutToken,
        shopifyProductIds,
      ],
    }),
  ),
  savedVideoScripts: makeQuery(getSavedVideoScripts, (organization: string, useCaseId: number) => ({
    queryKey: ['organization', organization, 'usecase', useCaseId, 'get-saved-video-scripts'],
  })),
  spOptimizerFlowMessageExperimentsForSPOptimizerStore: makeQuery(
    getSPOptimizerFlowMessageExperimentsForSPOptimizerStore,
    (organization: string, spOptimizerStoreId: number | undefined) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiments',
        'for-sp-optimizer-store',
        spOptimizerStoreId,
      ],
    }),
  ),
  spOptimizerFlowMessageExperimentsForStore: makeQuery(
    getSPOptimizerFlowMessageExperimentsForStore,
    (organization: string | undefined, storeKey: string | undefined) => ({
      queryKey: ['organization', organization, 'sp-optimizer-flow-message-experiments', 'for-store', storeKey],
    }),
  ),
  spOptimizerFlowMessageExperimentMetricsForSPOptimizerStore: makeQuery(
    getSPOptimizerFlowMessageExperimentMetricsForSPOptimizerStore,
    (organization: string, spOptimizerStoreId: number) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiment-metrics',
        'for-sp-optimizer-store',
        spOptimizerStoreId,
      ],
    }),
  ),
  spOptimizerFlowMessageExperimentVariantAggregatedMetrics: makeQuery(
    getSPOptimizerFlowMessageExperimentVariantAggregatedMetrics,
    (
      organization: string,
      spOptimizerFlowMessageExperimentVariantId: number,
      days: number,
      startDate?: string,
      endDate?: string,
    ) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiment-variant',
        spOptimizerFlowMessageExperimentVariantId,
        'aggregate-metrics',
        days,
        'start-date',
        startDate,
        'end-date',
        endDate,
      ],
    }),
  ),
  spOptimizerStoreAggregatedSummaryFlowMetrics: makeQuery(
    getSPOptimizerStoreAggregatedSummaryFlowMetrics,
    (
      organization: string,
      spOptimizerStoreId: number,
      days: number,
      flowId?: number,
      flowMessageId?: number,
      flowMessageVariantId?: number,
      startDate?: string,
      endDate?: string,
    ) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-store-aggregated-summary-flow-metrics',
        spOptimizerStoreId,
        'flow',
        flowId,
        'flow-message',
        flowMessageId,
        'flow-message-variant',
        flowMessageVariantId,
        'aggregate-metrics',
        days,
        'start-date',
        startDate,
        'end-date',
        endDate,
      ],
    }),
  ),
  flowsForStoreKey: makeQuery(getFlowsForStoreKey, (storeKey: string) => ({
    queryKey: ['flows-for-store-key', storeKey],
  })),
  flowMessageForStoreKey: makeQuery(getFlowMessagesForStoreKey, (storeKey: string) => ({
    queryKey: ['flow-messages-for-store-key', storeKey],
  })),
  flowActionsForStoreKey: makeQuery(getFlowActionsForStoreKey, (storeKey: string) => ({
    queryKey: ['flow-actions-for-store-key', storeKey],
  })),
  flowMessageOptimizerStats: makeQuery(getFlowMessageOptimizerStats, (storeKey: string) => ({
    queryKey: ['flow-messages-optimizer-stats-for-store-key', storeKey],
  })),
  latestFlowExperimentPhaseForStoreKey: makeQuery(
    getLatestFlowExperimentPhaseForStoreKey,
    (organization: string, storeKey: string) => ({
      queryKey: ['organization', organization, 'latest-flow-experiment-phases-for-store-key', storeKey],
    }),
  ),
  spOptimizerFlowMessageExperimentVariantsForExperiment: makeQuery(
    getSPOptimizerFlowMessageExperimentVariantsForExperiment,
    (organization: string, spOptimizerFlowMessageExperimentId: number, onlyActive?: boolean) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiment-variants-for-experiment',
        spOptimizerFlowMessageExperimentId,
        'only-active',
        onlyActive,
      ],
    }),
  ),
  spOptimizerFlowMessageExperimentKlaviyoTemplates: makeQuery(
    getSPOptimizerFlowMessageExperimentKlaviyoTemplates,
    (organization: string, spOptimizerFlowMessageExperimentId: number) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiment-klaviyo-templates',
        spOptimizerFlowMessageExperimentId,
      ],
    }),
  ),
  organizationMembers: makeQuery(getOrganizationMembers, (slug: string) => ({
    queryKey: ['organization', slug, 'members'],
  })),
  pretesterInboxes: makeQuery(getPretesterInboxes, () => ({
    queryKey: ['pretester-inboxes'],
  })),
  klaviyoAccountMetricsInSync: makeQuery(
    getKlaviyoAccountMetricsInSync,
    (organization: string, klaviyoAccountId: number) => ({
      queryKey: ['organization', organization, 'klaviyo-account', klaviyoAccountId, 'metrics-in-sync'],
    }),
  ),
  optimizerCampaignDeliverabilityExperimentMetrics: makeQuery(
    getOptimizerCampaignDeliverabilityExperimentMetrics,
    (organization: string, spOptimizerStoreID: number, days: number, startDate?: string, endDate?: string) => ({
      queryKey: [
        'organization',
        organization,
        'optimizer-campaign-deliverability-experiment-results',
        spOptimizerStoreID,
        'aggregate-metrics',
        days,
        'start-date',
        startDate,
        'end-date',
        endDate,
      ],
    }),
  ),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as const satisfies Record<string, (...args: any[]) => WithRequired<QueryOptions<any>, 'queryKey' | 'queryFn'>>;
